import { UploadManager } from "@context-providers/file-upload/upload-manager";
import { createUploadManager, waitForConnection } from "@context-providers/file-upload/upload-manager-factory";
import { UploadManagerWorker } from "@context-providers/file-upload/upload-manager-worker";
import {
  FileUploadTask,
  FileUploadTaskContext,
} from "@custom-types/file-upload-types";
import {
  UpdateTaskProps,
  UploadManagerInterface,
} from "@custom-types/upload-manager-types";
import { GUID } from "@faro-lotv/ielement-types";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { store } from "@store/store";
import { useAppDispatch } from "@store/store-helper";
import { uploadTaskByIdSelector } from "@store/upload-tasks/upload-tasks-selector";
import {
  removeOne,
  setOne,
  updateOne,
} from "@store/upload-tasks/upload-tasks-slice";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * The File Upload Context provides an object that store all ongoing
 * file uploads and allows to manage them.
 *
 * WARNING: this type should not be used directly, it is only needed to implement
 * the FileUploadContextProvider and the hooks useFileUploader and useCancelUpload.
 */
type UploadContext = {
  uploadManager: UploadManagerInterface;
};

/** Context which provides the file upload functionalities */
const FileUploadContext = createContext<UploadContext | undefined>(
  undefined
);

/**
 * @returns a context useful to start, store monitor and complete file upload tasks.
 *
 * WARNING: this type should not be used directly, it is just exported
 * as an implementation detail to realize the FileUploadContextProvider.
 */
export function useFileUploadContext(): UploadContext {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("FileUploadContext is not initialized.");
  }
  return context;
}

/**
 * @returns a provider component for the FileUploadContext
 * All children of this component may start, monitor, and finish file uploads
 * and the uploads are stored in the context, therefore they outlive any particular
 * mode or component.
 */
export function FileUploadContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const dispatch = useAppDispatch();
  /**
   * Standard upload manager used as fallback when the worker upload manager fails to connect to its worker.
   */
  const [uploadManagerFallback, setUploadManagerFallback] = useState<UploadManager | undefined>(undefined);

  /**
   * Creates a new upload task in store
   *
   * @param id An unique id of the task
   * @param file The file which is uploading
   * @param isSilent Whether progress toast notifications should be hidden
   * @param context Additional information of the file upload task
   */
  const startTaskInStore = useCallback(
    (
      id: GUID,
      file: File,
      isSilent: boolean,
      context: FileUploadTaskContext
    ) => {
      const isoDate = new Date().toISOString();

      const fileUploadTask: FileUploadTask = {
        id,
        fileName: file.name,
        createdAt: isoDate,
        status: BackgroundTaskState.created,
        progress: 0,
        context,
        isSilent,
      };
      dispatch(setOne(fileUploadTask));
    },
    [dispatch]
  );

  /**
   * Gets an upload task from store
   *
   * @params id The unique id of the task
   */
  const getTaskFromStore = useCallback(
    (id: GUID): FileUploadTask | undefined => {
      const selector = uploadTaskByIdSelector(id);
      return selector(store.getState());
    }, []
  );

  /**
   * Updates an upload task in store
   *
   * @params id The unique id of the task
   * @params propsToUpdate An object containing the property to update
   */
  const updateTaskInStore = useCallback(
    (id: GUID, propsToUpdate: UpdateTaskProps) => {
      const task = uploadTaskByIdSelector(id);
      if (task === undefined) {
        return;
      }

      dispatch(updateOne({ id, changes: propsToUpdate }));
    },
    [dispatch]
  );

  /**
   * Remove an upload task from store
   *
   * @params id The unique id of the task
   */
  const removeTaskFromStore = useCallback(
    (id: GUID) => {
      dispatch(removeOne(id));
    },
    [dispatch]
  );

  /**
   * The upload manager used to handle multiple files upload
   */
  const uploadManager = useMemo(
    () => createUploadManager(getTaskFromStore, startTaskInStore, updateTaskInStore, removeTaskFromStore),
    [getTaskFromStore, startTaskInStore, updateTaskInStore, removeTaskFromStore]
  );

  // Check for a worker upload manager if it was able to connect to its worker.
  // Use a standard upload manager as fallback if not.
  useEffect(() => {
    async function connect(): Promise<void> {
      if (uploadManager instanceof UploadManagerWorker) {
        const fallback = await waitForConnection(
          getTaskFromStore,
          startTaskInStore,
          updateTaskInStore,
          removeTaskFromStore,
          uploadManager
        );
        if (fallback !== undefined) {
          setUploadManagerFallback(fallback);
        }
      }
    }

    void connect();
  }, [removeTaskFromStore, setUploadManagerFallback, getTaskFromStore, startTaskInStore, uploadManager, updateTaskInStore]);

  return (
    <FileUploadContext.Provider value={{ uploadManager: uploadManagerFallback ?? uploadManager }}>
      {children}
    </FileUploadContext.Provider>
  );
}
